import React, { useState, useEffect, useMemo } from 'react';
import { Trash2, Lock, Plus, Calendar, PiggyBank, ArrowDownRight, ArrowUpRight, ShieldAlert, Banknote, Landmark } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:1000/api';

const AccountBook = ({ records, numberLinks, expenses = [], token, onRecordChanged }) => {
  const [selectedDate, setSelectedDate] = useState(new Date().toLocaleDateString('en-CA', {
    timeZone: 'Asia/Kolkata'
  }));
  const [desc, setDesc] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [isShiftClosed, setIsShiftClosed] = useState(false);

  // 1. Fetch Shift Status for the date chosen in the "Add Expense" form
  useEffect(() => {
    const checkShift = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${API_URL}/shift/${selectedDate}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsShiftClosed(res.data.is_closed);
      } catch (err) {
        console.error(err);
      }
    };
    checkShift();
  }, [selectedDate, token]);

  // 2. Build the Unified running Ledger Sheet (All-Time)
  const ledgerData = useMemo(() => {
    const list = [];

    // Step A: Group all PUC and Number Link collections by date
    const collectionsByDate = {};
    
    records.forEach(r => {
      const dateStr = new Date(r.record_date).toLocaleDateString('en-CA');
      if (!collectionsByDate[dateStr]) {
        collectionsByDate[dateStr] = { cash: 0, paytm: 0 };
      }
      if (r.payment_method === 'paytm') {
        collectionsByDate[dateStr].paytm += Number(r.amount);
      } else {
        collectionsByDate[dateStr].cash += Number(r.amount);
      }
    });

    numberLinks.forEach(r => {
      const dateStr = new Date(r.record_date).toLocaleDateString('en-CA');
      if (!collectionsByDate[dateStr]) {
        collectionsByDate[dateStr] = { cash: 0, paytm: 0 };
      }
      if (r.payment_method === 'paytm') {
        collectionsByDate[dateStr].paytm += Number(r.amount);
      } else {
        collectionsByDate[dateStr].cash += Number(r.amount);
      }
    });

    // Push daily collection totals as "In" entries
    Object.keys(collectionsByDate).forEach(dateStr => {
      const dayData = collectionsByDate[dateStr];
      const total = dayData.cash + dayData.paytm;
      if (total > 0) {
        list.push({
          id: `col-${dateStr}`,
          date: dateStr,
          description: 'Daily Collections (Auto)',
          type: 'in',
          payment_method: dayData.cash > 0 && dayData.paytm > 0 ? 'split' : (dayData.paytm > 0 ? 'paytm' : 'cash'),
          cashAmount: dayData.cash,
          paytmAmount: dayData.paytm,
          amount: total,
          isExpense: false,
          created_at: new Date(dateStr).getTime() // Sorting key
        });
      }
    });

    // Step B: Push all logged expenses as "Out" entries
    expenses.forEach(e => {
      list.push({
        id: `exp-${e.id}`,
        date: new Date(e.expense_date).toLocaleDateString('en-CA'),
        description: e.description,
        type: 'out',
        payment_method: e.payment_method || 'cash',
        cashAmount: e.payment_method === 'paytm' ? 0 : Number(e.amount),
        paytmAmount: e.payment_method === 'paytm' ? Number(e.amount) : 0,
        amount: Number(e.amount),
        isExpense: true,
        created_at: new Date(e.created_at).getTime(), // Sorting key
        dbId: e.id
      });
    });

    // Step C: Sort entries ascending (oldest first) to compute running balances
    list.sort((a, b) => {
      if (a.date !== b.date) {
        return new Date(a.date) - new Date(b.date);
      }
      return a.created_at - b.created_at;
    });

    // Compute running balances
    let runningBalance = 0;
    let runningCash = 0;
    let runningPaytm = 0;

    const computedList = list.map(item => {
      if (item.type === 'in') {
        runningBalance += item.amount;
        runningCash += item.cashAmount;
        runningPaytm += item.paytmAmount;
      } else {
        runningBalance -= item.amount;
        runningCash -= item.cashAmount;
        runningPaytm -= item.paytmAmount;
      }
      return {
        ...item,
        balance: runningBalance,
        cashBalance: runningCash,
        paytmBalance: runningPaytm
      };
    });

    // Step D: Return sorted descending (newest first) for rendering
    return computedList.reverse();
  }, [records, numberLinks, expenses]);

  // 3. Compute All-Time Aggregates
  const allTimeStats = useMemo(() => {
    // Total Collections
    const pucTotal = records.reduce((acc, r) => acc + Number(r.amount), 0);
    const nlTotal = numberLinks.reduce((acc, r) => acc + Number(r.amount), 0);
    const collectionsTotal = pucTotal + nlTotal;

    const collectionsCash = records.filter(r => r.payment_method === 'cash' || !r.payment_method).reduce((acc, r) => acc + Number(r.amount), 0) +
                            numberLinks.filter(r => r.payment_method === 'cash' || !r.payment_method).reduce((acc, r) => acc + Number(r.amount), 0);
    const collectionsPaytm = collectionsTotal - collectionsCash;

    // Total Expenses
    const expensesTotal = expenses.reduce((acc, e) => acc + Number(e.amount), 0);
    const expensesCash = expenses.filter(e => e.payment_method === 'cash' || !e.payment_method).reduce((acc, e) => acc + Number(e.amount), 0);
    const expensesPaytm = expensesTotal - expensesCash;

    // Net Balances
    const netBalance = collectionsTotal - expensesTotal;
    const netCash = collectionsCash - expensesCash;
    const netPaytm = collectionsPaytm - expensesPaytm;

    return {
      collectionsTotal,
      collectionsCash,
      collectionsPaytm,
      expensesTotal,
      expensesCash,
      expensesPaytm,
      netBalance,
      netCash,
      netPaytm
    };
  }, [records, numberLinks, expenses]);

  // 4. Handlers
  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!desc || !amount) return;

    try {
      const payload = {
        description: desc,
        amount: Number(amount),
        payment_method: paymentMethod,
        expense_date: selectedDate
      };
      await axios.post(`${API_URL}/expenses`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDesc('');
      setAmount('');
      if (onRecordChanged) onRecordChanged('Expense added successfully!');
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to add expense';
      if (onRecordChanged) onRecordChanged(msg, true);
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;
    try {
      await axios.delete(`${API_URL}/expenses/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (onRecordChanged) onRecordChanged('Expense deleted successfully!');
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to delete expense';
      if (onRecordChanged) onRecordChanged(msg, true);
    }
  };

  return (
    <div className="fade-in">
      {/* Top Header */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
          <div className="metric-icon" style={{ minWidth: '64px', background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2), rgba(168, 85, 247, 0.2))', color: '#6366f1', borderColor: 'rgba(99, 102, 241, 0.3)' }}>
            <Landmark size={28} />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.25rem' }}>Account Ledger Sheet</h2>
            <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-muted)' }}>Complete running ledger showing collections, expenses, and balances</p>
          </div>
        </div>
      </div>

      {/* Main KPI Summary (All-Time Ledger Balance) */}
      <div className="form-grid" style={{ gap: '1.5rem', marginBottom: '1.5rem', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
        {/* KPI 1: Net Account Balance */}
        <div className="dash-card" style={{ marginBottom: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Ledger Balance</div>
              <div style={{ fontSize: '2.25rem', fontWeight: 800, color: allTimeStats.netBalance >= 0 ? '#10b981' : '#ef4444', marginTop: '0.25rem' }}>
                ₹{allTimeStats.netBalance.toFixed(2)}
              </div>
            </div>
            <div style={{ padding: '8px', borderRadius: '8px', background: 'rgba(99,102,241,0.1)', color: '#6366f1' }}>
              <Banknote size={24} />
            </div>
          </div>
          <div style={{ borderTop: '1px solid var(--card-border)', marginTop: '1rem', paddingTop: '0.75rem', display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            <span>Total In: <strong>₹{allTimeStats.collectionsTotal.toFixed(2)}</strong></span>
            <span>Total Out: <strong>₹{allTimeStats.expensesTotal.toFixed(2)}</strong></span>
          </div>
        </div>

        {/* KPI 2: Cash Balance */}
        <div className="dash-card" style={{ marginBottom: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Cash In Hand</div>
              <div style={{ fontSize: '2.25rem', fontWeight: 800, color: allTimeStats.netCash >= 0 ? '#10b981' : '#ef4444', marginTop: '0.25rem' }}>
                ₹{allTimeStats.netCash.toFixed(2)}
              </div>
            </div>
            <div style={{ padding: '8px', borderRadius: '8px', background: 'rgba(16,185,129,0.1)', color: '#10b981' }}>
              <ArrowUpRight size={24} />
            </div>
          </div>
          <div style={{ borderTop: '1px solid var(--card-border)', marginTop: '1rem', paddingTop: '0.75rem', display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            <span>Cash In: <strong>₹{allTimeStats.collectionsCash.toFixed(2)}</strong></span>
            <span>Expenses Paid: <strong>₹{allTimeStats.expensesCash.toFixed(2)}</strong></span>
          </div>
        </div>

        {/* KPI 3: Paytm Balance */}
        <div className="dash-card" style={{ marginBottom: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Paytm / Online Balance</div>
              <div style={{ fontSize: '2.25rem', fontWeight: 800, color: allTimeStats.netPaytm >= 0 ? '#3b82f6' : '#ef4444', marginTop: '0.25rem' }}>
                ₹{allTimeStats.netPaytm.toFixed(2)}
              </div>
            </div>
            <div style={{ padding: '8px', borderRadius: '8px', background: 'rgba(59,130,246,0.1)', color: '#3b82f6' }}>
              <PiggyBank size={24} />
            </div>
          </div>
          <div style={{ borderTop: '1px solid var(--card-border)', marginTop: '1rem', paddingTop: '0.75rem', display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            <span>Online In: <strong>₹{allTimeStats.collectionsPaytm.toFixed(2)}</strong></span>
            <span>Online Out: <strong>₹{allTimeStats.expensesPaytm.toFixed(2)}</strong></span>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem', alignItems: 'start' }} className="grid-responsive-ledger">
        {/* Style helper for desktop layout */}
        <style dangerouslySetInnerHTML={{__html: `
          @media (min-width: 1024px) {
            .grid-responsive-ledger {
              grid-template-columns: 350px 1fr !important;
            }
          }
        `}} />

        {/* Column 1: Add Expense Form */}
        <div className="dash-card" style={{ marginBottom: 0 }}>
          <div className="card-header" style={{ marginBottom: '1.25rem' }}>
            <h3 style={{ fontSize: '1.1rem' }}>Log Expense Entry</h3>
          </div>
          <form onSubmit={handleAddExpense}>
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label>Description</label>
              <input 
                type="text" 
                className="form-control"
                placeholder="e.g. Tea & snacks, Paper rolls"
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                required
              />
            </div>
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label>Amount (₹)</label>
              <input 
                type="number" 
                step="0.01"
                min="0.01"
                className="form-control"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
            </div>
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label>Expense Date</label>
              <input 
                type="date" 
                className="form-control"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                required
              />
            </div>
            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label>Payment Method</label>
              <select 
                className="form-control"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                <option value="cash">Cash</option>
                <option value="paytm">Paytm / Online</option>
              </select>
            </div>
            
            {isShiftClosed && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', padding: '8px 12px', borderRadius: '8px', fontSize: '0.85rem', marginBottom: '1rem' }}>
                <ShieldAlert size={16} />
                <span>Selected date's shift is closed!</span>
              </div>
            )}
            
            <button 
              type="submit" 
              className="btn-primary" 
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
              disabled={isShiftClosed}
            >
              <Plus size={18} /> Add Entry
            </button>
          </form>
        </div>

        {/* Column 2: The Continuous All-Time Ledger Sheet */}
        <div className="dash-card" style={{ marginBottom: 0 }}>
          <div className="card-header" style={{ marginBottom: '1.25rem' }}>
            <h3 style={{ fontSize: '1.1rem' }}>Ledger Book Statement</h3>
          </div>
          
          <div className="table-responsive" style={{ border: 'none', background: 'transparent' }}>
            <table className="mobile-cards">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Payment</th>
                  <th>Flow</th>
                  <th>Balance</th>
                  <th style={{ width: '70px', textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {ledgerData.length > 0 ? (
                  ledgerData.map(item => {
                    const isCredit = item.type === 'in';
                    const displayDate = new Date(item.date).toLocaleDateString('en-GB');
                    
                    return (
                      <tr key={item.id} style={isCredit ? {} : { background: 'rgba(239, 68, 68, 0.015)' }}>
                        <td data-label="Date" style={{ fontWeight: 600 }}>{displayDate}</td>
                        <td data-label="Description" style={{ fontWeight: 600, color: 'var(--text-main)' }}>
                          {item.description}
                        </td>
                        <td data-label="Payment">
                          {item.payment_method === 'split' ? (
                            <span style={{ fontSize: '0.75rem', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <span className="badge badge-petrol" style={{ width: 'fit-content', padding: '1px 4px' }}>C: ₹{item.cashAmount}</span>
                              <span className="badge badge-cng" style={{ width: 'fit-content', padding: '1px 4px' }}>P: ₹{item.paytmAmount}</span>
                            </span>
                          ) : (
                            <span className={`badge ${item.payment_method === 'paytm' ? 'badge-cng' : 'badge-petrol'}`}>
                              {item.payment_method}
                            </span>
                          )}
                        </td>
                        <td data-label="Flow" style={{ fontWeight: 700, color: isCredit ? '#10b981' : '#ef4444', fontSize: '1.05rem' }}>
                          {isCredit ? '+' : '-'}₹{item.amount.toFixed(2)}
                        </td>
                        <td data-label="Balance" style={{ fontWeight: 700, color: 'var(--primary)' }}>
                          ₹{item.balance.toFixed(2)}
                          <div style={{ fontSize: '0.7rem', fontWeight: 400, color: 'var(--text-muted)', marginTop: '2px' }}>
                            (C: {item.cashBalance.toFixed(0)} | P: {item.paytmBalance.toFixed(0)})
                          </div>
                        </td>
                        <td data-label="Action" style={{ textAlign: 'right' }}>
                          {item.isExpense ? (
                            <button 
                              className="action-btn delete-btn"
                              onClick={() => handleDeleteExpense(item.dbId)}
                              title="Delete Expense"
                            >
                              <Trash2 size={13} />
                            </button>
                          ) : (
                            <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                      Ledger is empty. No collections or expenses recorded yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountBook;
